import { supabase } from './supabase.ts'

// Une definition par chiffre, cote base. Le front ne recalcule rien : il
// lit. Toute agregation faite ici serait une seconde definition du meme
// chiffre, donc une divergence garantie a terme.
//
// Les tables ne sont pas dans public : chaque requete nomme son schema.
// Cote Supabase, ces schemas doivent etre declares dans
// Settings > API > Exposed schemas, sinon PostgREST ne les sert pas.
const ops = () => supabase!.schema('ops')

export interface SanteConnecteur {
  connector: string
  last_ok_at: string | null
  last_run_at: string | null
  last_status: string | null
  last_error: string | null
  hours_since_ok: number | null
}

export async function santeConnecteurs(): Promise<SanteConnecteur[]> {
  if (!supabase) return []
  const { data, error } = await ops().from('connector_health').select('*').order('connector')
  if (error) throw error
  return data ?? []
}

export interface Alerte {
  id: string
  kind: string
  severity: 'info' | 'warn' | 'critical'
  title: string
  body: string | null
  status: 'new' | 'seen' | 'handled' | 'ignored'
  created_at: string
  evidence?: Record<string, any> | null
  handled_at?: string | null
  ignore_reason?: string | null
}

export async function fileAtraiter(): Promise<Alerte[]> {
  if (!supabase) return []
  const { data, error } = await ops()
    .from('alerts').select('id,kind,severity,title,body,status,created_at,evidence')
    .in('status', ['new', 'seen'])
    .order('created_at', { ascending: false })
  if (error) throw error
  // La gravite est un texte : triee par la base, « warn » passerait devant « critical ».
  const rang: Record<string, number> = { critical: 0, warn: 1, info: 2 }
  return (data ?? []).sort((a, b) => (rang[a.severity] ?? 3) - (rang[b.severity] ?? 3))
}

/** Ce qui a ete traite ou ignore ces 7 derniers jours : la trace de la file. */
export async function traitesRecemment(): Promise<Alerte[]> {
  if (!supabase) return []
  const { data, error } = await ops()
    .from('alerts').select('id,kind,severity,title,body,status,created_at,handled_at,ignore_reason,evidence')
    .in('status', ['handled', 'ignored']).gte('handled_at', new Date(Date.now() - 7 * 86400e3).toISOString())
    .order('handled_at', { ascending: false })
  if (error) throw error
  return data ?? []
}

/** Ignorer exige une raison : la base le refuse, l interface aussi. */
export async function traiterAlerte(id: string, statut: 'handled' | 'ignored', raison?: string) {
  if (!supabase) throw new Error('base non configuree')
  if (statut === 'ignored' && !raison?.trim()) {
    throw new Error('Ignorer une alerte demande une raison.')
  }
  const { error } = await ops().from('alerts')
    .update({ status: statut, ignore_reason: raison ?? null, handled_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
}

export interface Decision {
  id: string; title: string; rationale: string; review_at: string; status: string
}

export async function decisionsARevoir(): Promise<Decision[]> {
  if (!supabase) return []
  const { data, error } = await ops()
    .from('decisions').select('id,title,rationale,review_at,status')
    .eq('status', 'open').lte('review_at', new Date().toISOString().slice(0, 10))
    .order('review_at')
  if (error) throw error
  return data ?? []
}

// --- Revenue ----------------------------------------------------------------

export interface Modele { id: string; display_name: string; market: string; active: boolean }

export async function modeles(): Promise<Modele[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('core').from('models')
    .select('id,display_name,market,active').order('display_name')
  if (error) throw error
  return data ?? []
}

/** Portfolio AI (Agency OS) — modèles creator_type = ai. */
export interface ModeleAi {
  id: string
  slug: string
  display_name: string
  market: string
  active: boolean
  stage: string
  creator_type: string
  lifecycle_stage: string
  objective: string | null
  strategy: Record<string, unknown>
  assigned_bot: string | null
  autonomy_level: string
  health: string
  risk: string
  updated_at: string
}

export async function portfolioAi(): Promise<ModeleAi[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('core').rpc('portfolio_ai')
  if (error) throw error
  return (data as ModeleAi[]) ?? []
}

export interface AttentionItem {
  id: string
  attention_id: string
  model_id: string | null
  type: string
  observation: string
  severity: string
  data_quality: string
  suggested_domain: string | null
  assigned_role: string | null
  wake_grok: boolean
  evidence: Record<string, unknown>
  created_at: string
}

export async function attentionOuverte(wakeOnly = true): Promise<AttentionItem[]> {
  if (!supabase) return []
  const { data, error } = await ops().rpc('attention_open', { p_wake_only: wakeOnly })
  if (error) throw error
  return (data as AttentionItem[]) ?? []
}

export async function experiencesOuvertes(): Promise<{ id: string; model_id: string | null; hypothesis: string; status: string; primary_kpi: string }[]> {
  if (!supabase) return []
  const { data, error } = await ops().from('experiments')
    .select('id,model_id,hypothesis,status,primary_kpi')
    .in('status', ['planned', 'running'])
    .order('created_at', { ascending: false })
  if (error) throw error
  return data ?? []
}

export interface DecisionBot {
  id: string
  title: string
  rationale: string
  actor_bot: string
  model_id: string | null
  confidence: string | null
  status: string
  review_at: string
  evidence: Record<string, unknown>
  created_at: string
}

export async function decisionsBotPortfolio(limit = 20): Promise<DecisionBot[]> {
  if (!supabase) return []
  const { data, error } = await ops().rpc('portfolio_bot_decisions', { p_limit: limit })
  if (error) throw error
  return (data as DecisionBot[]) ?? []
}

export interface EcoModeleAi {
  model_id: string
  display_name: string
  net_7d: number
  subs_7d: number
  cost_7d: number
  profit_7d: number
}

export async function portfolioAiEconomics(days = 7): Promise<EcoModeleAi[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('core').rpc('portfolio_ai_economics', { p_days: days })
  if (error) throw error
  return (data as EcoModeleAi[]) ?? []
}

/** onlyfans : transactions Infloww. mym : totaux par jour de MyPuls. */
export type Plateforme = 'onlyfans' | 'mym' | string

export interface RevenuModele {
  model_id: string; plateforme: Plateforme; currency: string; net: number; net_disponible: number
  net_en_attente: number; transactions: number; nouveaux_subs: number | null
}

const nombreOuNull = (v: unknown) => (v == null ? null : Number(v))

/** Total d une periode par modele, plateforme et devise, calcule en base : l ecran n additionne rien. */
export async function revenuPeriode(debut: string, fin: string): Promise<RevenuModele[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('finance').rpc('revenu_periode', { p_debut: debut, p_fin: fin })
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    ...r, net: Number(r.net), net_disponible: Number(r.net_disponible), net_en_attente: Number(r.net_en_attente),
    transactions: Number(r.transactions), nouveaux_subs: nombreOuNull(r.nouveaux_subs),
  }))
}

export interface RevenuMois { model_id: string; mois: string; plateforme: Plateforme; currency: string; net: number; transactions: number }

export async function revenuMensuel(depuis: string): Promise<RevenuMois[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('finance').from('revenu_mensuel')
    .select('model_id,mois,plateforme,currency,net,transactions').gte('mois', depuis).order('mois')
  if (error) throw error
  return (data ?? []).map((r: any) => ({ ...r, net: Number(r.net), transactions: Number(r.transactions) }))
}

export interface Indicateur {
  model_id: string; plateforme: Plateforme; currency: string; net: number; net_precedent: number
  subs: number | null; subs_precedents: number | null; source_subs: string
}

/** Cash et nouveaux subs d une periode, et ceux de la periode precedente de meme longueur. */
export async function indicateurs(debut: string, fin: string): Promise<Indicateur[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('finance').rpc('indicateurs', { p_debut: debut, p_fin: fin })
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    ...r, net: Number(r.net), net_precedent: Number(r.net_precedent),
    subs: nombreOuNull(r.subs), subs_precedents: nombreOuNull(r.subs_precedents),
  }))
}

/**
 * Relancer une synchro tout de suite, sans attendre le cron (proprietaire seulement).
 * « infloww-liens » lit les liens et suit l equipe sans relire les subs : quelques secondes.
 */
export async function relancerSynchro(connecteur: string): Promise<{ lance: boolean; raison?: string }> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await ops().rpc('relancer_synchro', { p_connecteur: connecteur })
  if (error) throw error
  return data as { lance: boolean; raison?: string }
}

/** Derniere synchronisation reussie de chaque connecteur : la fraicheur affichee vient d ici. */
export async function fraicheurs(): Promise<Record<string, Date | null>> {
  if (!supabase) return {}
  const { data, error } = await ops().from('connector_health').select('connector,last_ok_at')
  if (error) throw error
  return Object.fromEntries((data ?? []).map((r: any) => [r.connector, r.last_ok_at ? new Date(r.last_ok_at) : null]))
}

// --- Acquisition -------------------------------------------------------------

export interface CanalAcquisition { canal: string; subs: number; subs_precedents: number; revenu_net: number; liens_actifs: number; subs_payes: number; cout_va_usd: number }

/** Subs par canal (liens de tracking Infloww) sur la periode et la precedente. Direction seulement (RLS). */
export async function acquisitionCanaux(debut: string, fin: string): Promise<CanalAcquisition[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('traffic').rpc('acquisition_canaux', { p_debut: debut, p_fin: fin })
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    canal: r.canal, subs: Number(r.subs), subs_precedents: Number(r.subs_precedents),
    revenu_net: Number(r.revenu_net), liens_actifs: Number(r.liens_actifs),
    subs_payes: Number(r.subs_payes), cout_va_usd: Number(r.cout_va_usd),
  }))
}

export interface StatCompte {
  account_id: string; platform: string; handle: string; status: string; status_reason: string | null
  collecte_at: string | null; person_id: string | null; followers: number | null; followers_il_y_a_7j: number | null
  posts_7j: number; vues_7j: number | null; mediane_vues_14j: number | null
}

export async function statsComptes(): Promise<StatCompte[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('traffic').from('stats_comptes')
    .select('account_id,platform,handle,status,status_reason,collecte_at,person_id,followers,followers_il_y_a_7j,posts_7j,vues_7j,mediane_vues_14j')
    .eq('collecte', true).order('handle')
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    ...r, followers: nombreOuNull(r.followers), followers_il_y_a_7j: nombreOuNull(r.followers_il_y_a_7j),
    posts_7j: Number(r.posts_7j), vues_7j: nombreOuNull(r.vues_7j), mediane_vues_14j: nombreOuNull(r.mediane_vues_14j),
  }))
}

export interface PostSubs {
  post_id: string; handle: string; person_id: string | null; published_at: string; url: string
  vues: number | null; subs_estimes: number; revenu_estime: number; propre: boolean
}

/** Estimation par le timing : posts de la periode classes par subs estimes. Direction seulement (RLS). */
export async function postsSubs(debut: string, fin: string, limite = 20): Promise<PostSubs[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('traffic').from('posts_subs')
    .select('post_id,handle,person_id,published_at,url,vues,subs_estimes,revenu_estime,propre')
    .gte('published_at', debut).lte('published_at', `${fin}T23:59:59Z`)
    .order('subs_estimes', { ascending: false }).limit(limite)
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    ...r, vues: nombreOuNull(r.vues), subs_estimes: Number(r.subs_estimes), revenu_estime: Number(r.revenu_estime),
  }))
}

export async function personnes(): Promise<Record<string, string>> {
  if (!supabase) return {}
  const { data, error } = await supabase.schema('core').from('people').select('id,full_name')
  if (error) throw error
  return Object.fromEntries((data ?? []).map((r: any) => [r.id, r.full_name]))
}

// --- Equipe et paie ------------------------------------------------------------

export interface PeriodePaie {
  period_id: string; starts_on: string; ends_on: string; status: string; debut: string; fin: string
  terminee: boolean; lignes: number; brouillons: number; total_usd: number
}

export async function periodesPaie(): Promise<PeriodePaie[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('finance').from('periodes_paie').select('*').order('starts_on')
  if (error) throw error
  return (data ?? []).map((r: any) => ({ ...r, lignes: Number(r.lignes), brouillons: Number(r.brouillons), total_usd: Number(r.total_usd) }))
}

export interface LignePaie { id: string; period_id: string; person_id: string; amount: number; status: string; basis: any }

export async function lignesPaie(periodes: string[]): Promise<LignePaie[]> {
  if (!supabase || periodes.length === 0) return []
  const { data, error } = await supabase.schema('finance').from('payout_lines')
    .select('id,period_id,person_id,amount,status,basis').in('period_id', periodes).order('amount', { ascending: false })
  if (error) throw error
  return (data ?? []).map((r: any) => ({ ...r, amount: Number(r.amount) }))
}

export async function validerPaie(periode: string) {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('finance').rpc('valider_paie', { p_period: periode })
  if (error) throw error
  return data
}

export async function marquerPayee(periode: string) {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('finance').rpc('marquer_payee', { p_period: periode })
  if (error) throw error
  return data
}

export interface FicheVa {
  person_id: string; full_name: string; manager_id: string | null; active: boolean; started_at: string | null
  liens: string[]; comptes: string[]; subs_30j: number; subs_total: number
  rapporte_usd: number; paye_usd: number; a_payer_usd: number
}

export async function fichesVa(): Promise<FicheVa[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('finance').from('fiche_va').select('*').eq('active', true).order('full_name')
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    ...r, subs_30j: Number(r.subs_30j), subs_total: Number(r.subs_total),
    rapporte_usd: Number(r.rapporte_usd), paye_usd: Number(r.paye_usd), a_payer_usd: Number(r.a_payer_usd),
  }))
}

export interface NomAVoir { person_id: string; full_name: string; raisons: string[] }

/** Manageuse : sa propre equipe. Direction : l equipe de la manageuse donnee. */
export async function troisNoms(manager?: string): Promise<NomAVoir[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('ops').rpc('trois_noms', manager ? { p_manager: manager } : {})
  if (error) throw error
  return data ?? []
}

export interface TableauManager {
  manager_id: string; full_name: string; va_actives: number; va_qui_performent: number; subs_jour_equipe: number | null
  posts_7j: number | null; vues_7j: number | null; va_sans_compte: number; fiches_7j: number
}

export async function tableauManagers(): Promise<TableauManager[]> {
  if (!supabase) return []
  const { data, error } = await ops().from('tableau_managers').select('*').order('full_name')
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    ...r, va_actives: Number(r.va_actives), va_qui_performent: Number(r.va_qui_performent),
    subs_jour_equipe: nombreOuNull(r.subs_jour_equipe), posts_7j: nombreOuNull(r.posts_7j), vues_7j: nombreOuNull(r.vues_7j),
    va_sans_compte: Number(r.va_sans_compte), fiches_7j: Number(r.fiches_7j),
  }))
}

// --- Qui suis-je, et ce que chaque role voit ------------------------------------

export interface Moi { id: string; full_name: string; role: 'owner' | 'partner' | 'manager' | 'va' | 'chatter' | string }

/** La fiche de la personne connectee (la RLS ne montre que la sienne a une VA). */
export async function moi(): Promise<Moi | null> {
  if (!supabase) return null
  const { data: u } = await supabase.auth.getUser()
  if (!u.user) return null
  const { data, error } = await supabase.schema('core').from('people').select('id,full_name,role').eq('auth_user_id', u.user.id).maybeSingle()
  if (error) throw error
  return data
}

export interface ChampRapport { cle: string; libelle: string; type: 'oui_non' | 'texte' }

/** Champs de la fiche du soir : reglage, propre a la manageuse s il existe. */
export async function champsRapport(manager: string): Promise<ChampRapport[]> {
  if (!supabase) return []
  const { data, error } = await ops().from('settings').select('scope,value,valid_from')
    .eq('key', 'rapport.manager.champs').in('scope', ['global', `manager:${manager}`])
    .lte('valid_from', new Date().toISOString()).order('valid_from', { ascending: false })
  if (error) throw error
  const lignes = (data ?? []).filter((r: any) => r.valid_from)
  const propre = lignes.find((r: any) => r.scope !== 'global')
  return ((propre ?? lignes[0])?.value ?? []) as ChampRapport[]
}

export interface SocialVa {
  person_id: string; full_name: string; comptes: number; comptes_introuvables: number; posts_7j: number
  vues_7j: number | null; abonnes: number | null; abonnes_gagnes_7j: number | null; activite_7j: number | null
}

export async function socialEquipe(): Promise<SocialVa[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('traffic').from('social_va')
    .select('person_id,full_name,comptes,comptes_introuvables,posts_7j,vues_7j,abonnes,abonnes_gagnes_7j,activite_7j').order('full_name')
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    ...r, comptes: Number(r.comptes), comptes_introuvables: Number(r.comptes_introuvables), posts_7j: Number(r.posts_7j),
    vues_7j: nombreOuNull(r.vues_7j), abonnes: nombreOuNull(r.abonnes), abonnes_gagnes_7j: nombreOuNull(r.abonnes_gagnes_7j),
    activite_7j: nombreOuNull(r.activite_7j),
  }))
}

export async function rapportsDuJour(jour: string): Promise<Record<string, Record<string, unknown>>> {
  if (!supabase) return {}
  const { data, error } = await ops().from('rapports_manager').select('person_id,champs').eq('jour', jour)
  if (error) throw error
  return Object.fromEntries((data ?? []).map((r: any) => [r.person_id, r.champs]))
}

export async function poserRapport(personne: string, jour: string, champs: Record<string, unknown>) {
  if (!supabase) throw new Error('base non configuree')
  const { error } = await ops().rpc('poser_rapport', { p_person: personne, p_jour: jour, p_champs: champs })
  if (error) throw error
}

export async function mesSubs(debut: string, fin: string): Promise<{ lien: string; subscribed_at: string }[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('traffic').rpc('mes_subs', { p_debut: debut, p_fin: fin })
  if (error) throw error
  return data ?? []
}

export async function semaineEnCours(): Promise<{ debut: string; fin: string } | null> {
  if (!supabase) return null
  const { data, error } = await supabase.schema('finance').rpc('semaine_en_cours')
  if (error) throw error
  return (data ?? [])[0] ?? null
}

/** Les lignes de paie de la personne connectee (la RLS ne rend que les siennes). */
export async function mesLignesPaie(moiId: string): Promise<LignePaie[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('finance').from('payout_lines')
    .select('id,period_id,person_id,amount,status,basis').eq('person_id', moiId).order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map((r: any) => ({ ...r, amount: Number(r.amount) }))
}

// --- Planche de style : series reelles --------------------------------------------

export interface RevenuJour { model_id: string; jour: string; plateforme: string; currency: string; net: number }

/** Revenu net par jour, modele, plateforme et devise (vue finance.revenu_net, rien n est additionne ici). */
export async function revenuJours(depuis: string): Promise<RevenuJour[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('finance').from('revenu_net')
    .select('model_id,jour,plateforme,currency,net').gte('jour', depuis).order('jour')
  if (error) throw error
  return (data ?? []).map((r: any) => ({ ...r, net: Number(r.net ?? 0) }))
}

export interface PerfVa { person_id: string; full_name: string; statut: string; subs_jour: number | null; jours_sans_sub: number | null }

export async function perfVa(): Promise<PerfVa[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('traffic').from('perf_va').select('person_id,full_name,statut,subs_jour,jours_sans_sub')
  if (error) throw error
  return (data ?? []).map((r: any) => ({ ...r, subs_jour: nombreOuNull(r.subs_jour), jours_sans_sub: nombreOuNull(r.jours_sans_sub) }))
}

// --- Tableau de bord ----------------------------------------------------------------

export interface TableauDeBord {
  periode: { debut: string; fin: string; debut_precedent: string; fin_precedent: string }
  cash: { plateforme: string; currency: string; net: number; net_precedent: number; disponible?: number; en_attente?: number
          ventes?: number; ventes_precedentes?: number; fans_payants?: number; panier_moyen?: number | null }[]
  subs: { liens: number; liens_precedents: number; mym: number | null; mym_precedents: number | null; rapporte: number }
  clics: { periode: number | null; precedente: number | null; historique_depuis: string | null; cumul_clics: number | null; cumul_subs: number | null }
  va: { actives: number; performent: number; onboarding: number; sous_objectif: number; ne_performent_pas: number }
  serie: { jour: string; cash: number; cash_mym: number | null; subs: number; clics: number | null }[]
  repartition: { type: string; currency: string; net: number; ventes: number }[]
  heures: { heure: number; net: number; ventes: number }[]
  top_va: { person_id: string; nom: string; subs: number; subs_precedents: number; rapporte: number | null }[]
  top_fans: { fan_id: string; nom: string | null; identifiant: string; modele: string; achats: number; net: number; dernier_achat: string }[]
  top_liens: { lien_id: string; nom: string; canal: string; va: string | null; clics: number | null; subs: number | null; conversion: number | null; subs_periode: number }[]
  comptes: { enregistres: number; releves: number }
}

/** Tout le tableau de bord en un appel, calcule en base (finance.tableau_de_bord). */
export async function tableauDeBord(debut: string, fin: string, modele: string | null): Promise<TableauDeBord> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('finance').rpc('tableau_de_bord', { p_debut: debut, p_fin: fin, p_model: modele })
  if (error) throw error
  // Postgres rend les numeric en chaines dans le JSON : on convertit une fois ici.
  return JSON.parse(JSON.stringify(data), (_k, v) => (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v) ? Number(v) : v))
}

// --- Revenus et acquisition detailles -------------------------------------------

const nombres = (data: unknown) => JSON.parse(JSON.stringify(data), (_k, v) => (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v) ? Number(v) : v))

export interface RevenusDetail {
  periode: { debut: string; fin: string; debut_precedent: string; fin_precedent: string }
  fraicheur: Record<string, string | null>
  totaux: { currency: string; net: number; net_precedent: number; disponible: number; en_attente: number; ventes: number; ventes_precedentes: number
            panier_moyen: number | null; fans_payants: number; nouveaux_fans: number; cash_nouveaux_fans: number; cash_fideles: number }[]
  mym: { plateforme: string; currency: string; net: number; net_precedent: number; nouveaux_subs: number | null; renouvellements: number | null }[]
  serie: { jour: string; net: number; types: Record<string, number>; ventes: number; mym: number | null }[]
  types: { type: string; net: number; ventes: number; net_precedent: number | null }[]
  modeles: { model_id: string; nom: string; marche: string; net: number; net_precedent: number; ventes: number; fans_payants: number }[]
  mois: { mois: string; net: number; mym: number | null }[]
  mois_en_cours: { net: number; meme_periode_mois_precedent: number; mois_precedent_complet: number; jours_ecoules: number; jours_du_mois: number }
  dernieres_ventes: { quand: string; net: number; currency: string; type: string; statut: string; fan: string; modele: string; nouveau_fan: boolean }[]
  top_fans: { fan_id: string; nom: string; modele: string; achats: number; net: number; dernier_achat: string; premier_achat: string | null }[]
}

export async function revenusDetail(debut: string, fin: string, modele: string | null): Promise<RevenusDetail> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('finance').rpc('revenus_detail', { p_debut: debut, p_fin: fin, p_model: modele })
  if (error) throw error
  return nombres(data)
}

export interface AcquisitionDetail {
  periode: { debut: string; fin: string }
  fraicheur: Record<string, string | null>
  totaux: { subs: number; subs_precedents: number; depense_nouveaux: number; depense_par_sub: number | null; fans_payants: number
            cout_va: number | null; subs_payes: number; clics: number | null; clics_precedents: number | null; clics_depuis: string | null
            cumul_clics: number | null; cumul_subs: number | null; liens_actifs: number; liens_qui_convertissent: number }
  serie: { jour: string; subs: number; clics: number | null; liens: Record<string, number> }[]
  top: { lien_id: string; nom: string }[]
  liens: { lien_id: string; nom: string; canal: string; statut: string; modele: string; cree_le: string | null; arrete_le: string | null; va: string | null
           clics: number | null; subs: number | null; conversion: number | null; subs_periode: number; subs_precedents: number; depense_periode: number
           dernier_sub: string | null; jours: number[] }[]
  canaux: { canal: string; subs: number; subs_precedents: number }[]
  va: { person_id: string; nom: string; subs: number; subs_precedents: number; depense: number | null }[]
  derniers_subs: { quand: string; lien: string; fan: string; depense: number; va: string | null }[]
}

export async function acquisitionDetail(debut: string, fin: string, modele: string | null): Promise<AcquisitionDetail> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('acquisition_detail', { p_debut: debut, p_fin: fin, p_model: modele })
  if (error) throw error
  return nombres(data)
}

// --- Equipe : profils, paie, gestes ----------------------------------------------

export interface LienVa {
  lien_id: string; nom: string; statut: string; canal: string; ouvert: boolean; du: string; au: string | null; modele: string
  clics: number | null; subs: number | null; conversion: number | null; subs_7j: number; ltv_par_sub: number | null; usd_par_sub: number | null
  dernier_sub: string | null
}
export interface CompteVa {
  account_id: string; plateforme: string; handle: string; statut: string; followers: number | null; gagnes_7j: number | null
  posts_7j: number; vues_7j: number | null; activite_7j: number | null; collecte_at: string | null
}
export interface ProfilVa {
  person_id: string; nom: string; role: string; marche: string | null; manager: string | null; actif: boolean; depuis: string | null; fin: string | null
  en_attente: boolean; onboarde_le: string | null; statut: string | null; subs_jour: number | null; jours_sans_sub: number | null
  premier_sub: string | null; dernier_sub: string | null; subs_7j: number; subs_7j_prec: number; subs_30j: number; subs_total: number
  depense: number; depense_30j: number; payants: number; depense_par_sub: number | null; serie: number[]
  liens: LienVa[]; comptes: CompteVa[]; paie_semaine: number | null; subs_semaine: number | null; paye: number; a_payer: number
}
export interface LigneSemaine {
  id: string; person_id: string; nom: string; montant: number; statut: string; regle: string; subs: number | null; subs_en_retard: number | null
  liens: { nom: string; subs: number; qualite: string; usd_par_sub: number; montant: number }[] | null
}
export interface SemainePaie {
  period_id: string; starts_on: string; ends_on: string; debut: string; fin: string; status: string; closed_at: string | null
  terminee: boolean; courante: boolean; total: number; subs: number; brouillons: number; lignes: LigneSemaine[]
}
export interface Equipe {
  fraicheur: Record<string, string | null>
  reglages: { objectif_subs_jour: number; tarif: { seuil_ltv_par_sub_usd: number; usd_bas: number; usd_haut: number } | null }
  resume: { actives: number; performent: number; onboarding: number; a_relancer: number; subs_7j: number; subs_7j_prec: number; subs_jour: number
            serie: { jour: string; subs: number }[]; depense_par_sub_30j: number | null; payants_30j: number; subs_30j: number
            subs_payes: number; cout_subs_payes: number; depense_subs_payes: number; cout_par_sub: number | null; va_avec_comptes: number }
  semaines: SemainePaie[]
  va: ProfilVa[]
  managers: { person_id: string; nom: string; va_actives: number; va_sans_compte: number; fiches_7j: number; posts_7j: number | null; vues_7j: number | null; paie_semaine: number | null }[]
  liens_sans_va: { lien_id: string; nom: string; source: string | null; statut: string; modele: string; clics: number | null; subs: number | null
                   cree_le: string | null; subs_30j: number; dernier_sub: string | null }[]
}

/** Toute l equipe en un appel (core.equipe) : profils, paie, liens sans VA. */
export async function equipe(): Promise<Equipe> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('core').rpc('equipe')
  if (error) throw error
  return nombres(data)
}

export interface FicheVaDetail {
  serie: { jour: string; subs: number; depense: number; clics: number | null }[]
  heures: number[]
  serie_24h: { heure: string; subs: number }[]; subs_24h: number; subs_24h_prec: number
  achats: Record<'abonnement' | 'messages' | 'tips' | 'posts' | 'lives', number>
  derniers_subs: { quand: string; fan: string; lien: string; depense: number; paie: string | null }[]
  meilleurs_fans: { fan: string; depense: number; abonne_le: string; dernier_achat: string | null; lien: string }[]
  paie: { starts_on: string; ends_on: string; statut: string; montant: number; regle: string; subs: number | null; subs_en_retard: number | null }[]
  posts: { handle: string; url: string; publie_le: string; vues: number | null }[]
}

export async function ficheVaDetail(id: string): Promise<FicheVaDetail> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('core').rpc('fiche_va_detail', { p_person: id })
  if (error) throw error
  return nombres(data)
}

export async function ajouterVa(nom: string, marche: string, lien: string | null): Promise<string> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('core').rpc('ajouter_va', { p_nom: nom, p_marche: marche, p_lien: lien })
  if (error) throw error
  return data as string
}

export async function rattacherLien(personne: string, lien: string): Promise<{ du: string; subs: number }> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('core').rpc('rattacher_lien', { p_person: personne, p_lien: lien })
  if (error) throw error
  return nombres(data)
}

export interface BilanComptes { crees: number; affectes: number; reaffectes: number; retires: number; inchanges: number; erreurs: { handle: string; raison: string }[] }

/** Ajoute (ou retire) des comptes a une VA, par son nom (core.enregistrer_comptes). */
export async function enregistrerComptes(comptes: { va?: string; plateforme: string; handle: string; modele?: string; retirer?: boolean }[]): Promise<BilanComptes> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('core').rpc('enregistrer_comptes', { p_comptes: comptes })
  if (error) throw error
  return data as BilanComptes
}

// --- Sante du systeme --------------------------------------------------------------

export interface Passage { debut: string; statut: string; duree_s: number | null; attendus: number | null; vus: number | null; ecrits: number | null; erreur: string | null }
export interface Sante {
  connecteurs: { connector: string; last_ok_at: string | null; last_run_at: string | null; last_status: string | null; last_error: string | null
                 hours_since_ok: number | null; surveille: boolean; seuil_h: number; reussis_24h: number; echecs_24h: number; passages: Passage[] }[]
  taches: { nom: string; planning: string; actif: boolean; dernier: { debut: string; statut: string; message: string | null } | null; echecs_24h: number }[]
  donnees: Record<'ventes' | 'subs' | 'fans' | 'liens' | 'comptes' | 'mym' | 'clics' | 'posts', Record<string, any>>
  credits: { solde: number; le: string } | null
  alertes_ouvertes: number
}

/** Tout l etat du systeme en un appel (ops.sante), direction seulement. */
export async function sante(): Promise<Sante> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await ops().rpc('sante')
  if (error) throw error
  return nombres(data)
}

// --- Chatting ------------------------------------------------------------------------

export interface Chatting {
  periode: { debut: string; fin: string }
  fraicheur: string | null
  ventes_depuis: string | null
  totaux: { messages: number; messages_prec: number; tips: number; tips_prec: number; autres: number; ventes: number; ventes_prec: number
            panier_message: number | null; fans_payants: number; nouveaux_payants: number; subs: number; subs_qui_achetent: number }
  serie: { jour: string; messages: number; tips: number; autres: number }[]
  heures: { heure: number; net: number; ventes: number }[]
  jours_semaine: { jour: number; net: number; ventes: number }[]
  a_relancer: { fan: string; total: number; achats: number; dernier: string; modele: string; jours: number }[]
  en_feu: { fan: string; net_48h: number; achats_48h: number; dernier: string; total: number; modele: string }[]
  a_convertir: { n: number; sur: number; liste: { fan: string; abonne_le: string; lien: string; va: string | null }[] }
  paliers: { palier: string; min: number; fans: number; total: number }[]
}

export async function chatting(debut: string, fin: string, modele: string | null): Promise<Chatting> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('finance').rpc('chatting', { p_debut: debut, p_fin: fin, p_model: modele })
  if (error) throw error
  return nombres(data)
}

// --- GeeLark -------------------------------------------------------------------------

export type EtatCompteGeelark = 'ok' | 'partiel' | 'deco' | 'absent' | 'checkpoint' | 'bloque' | 'banni'
export interface CompteGeelark {
  plateforme: string; handle: string | null; etat: EtatCompteGeelark; note: string | null
  source: 'etiquette' | 'dash'; maj_le: string
  login_email?: string | null; login_mdp?: string | null; login_2fa?: string | null
}
export interface TacheGeelark {
  id: string; telephone: string | null; nom: string | null; flotte: string | null; plan: string | null
  nature: 'warmup' | 'connexion' | 'post' | 'story' | 'profil' | 'creation' | 'autre'
  le: string; statut: number; duree_s: number | null; code: number | null; texte: string | null; explication: string | null
}
export interface TelephoneGeelark {
  id: string; nom: string; numero: string | null; flotte: string; groupe: string | null; marche: string | null
  proxy: string | null; proxy_libelle: string | null; statut: number | null; allume_depuis: string | null; cree_le: string | null
  remarque: string | null; etiquettes: string[]; os: string | null; appareil: string | null
  /** Telephone qui ne porte pas Instagram/TikTok : Reddit tenu par Svea, YouTube Shorts. */
  usage: { plateforme: string; libelle: string; tenu_par?: string } | null
  comptes: CompteGeelark[]; derniere_session: string | null; minutes_7j: number; sessions_7j: number; doublons_7j: number
  derniere_tache: { nature: TacheGeelark['nature']; plan: string | null; statut: number; le: string; code: number | null; texte: string | null; explication: string | null } | null
  taches_7j: { warmup_ok: number; warmup_echec: number; posts_ok: number; posts_echec: number; echecs: number }
  warmup_jours: { jour: string; ok: number; echec: number }[]
}
export interface DoublonGeelark {
  debut: string; secondes: number; certain: boolean
  a: { id: string; nom: string; hors_agence?: boolean; origines: ('ouverture' | 'tache')[] }
  b: { id: string; nom: string; hors_agence?: boolean; origines: ('ouverture' | 'tache')[] }
}
export interface SessionGeelark { telephone: string; nom: string; hors_agence?: boolean; proxy: string | null; debut: string; fin: string; minutes: number; origine: 'ouverture' | 'tache' }
export interface ProxyGeelark {
  cle: string; libelle: string; fournisseur: string | null; marche: string | null; telephones: number; hors_agence?: number
  flottes: Record<string, number> | null; allumes: { id: string; nom: string; depuis: string | null; hors_agence?: boolean }[]
  sessions_24h: number; minutes_24h: number; doublons: DoublonGeelark[]
  par_jour: { jour: string; sessions: number; doublons: number }[]
}
export interface Geelark {
  fraicheur: string | null
  plan: { expirationTime: number | null; profiles: number | null; availableProfiles: number | null; solde?: number; cadeau?: number; minutes?: number } | null
  depuis: { sessions: string | null; taches: string | null }
  resume: {
    telephones: number; allumes: number; comptes: Record<string, Partial<Record<EtatCompteGeelark | 'total', number>>> | null
    doublons_24h: number; doublons_7j: number; incertains_7j: number
    taches_24h: { reussies: number; echecs: number; en_cours: number; annulees: number }
    minutes_24h: number; minutes_7j: number
  }
  flottes: {
    nom: string; telephones: number; allumes: number; marches: string[] | null
    instagram: { ok: number; total: number }; tiktok: { ok: number; total: number }
    a_regler: number; warmup_aujourdhui: { ok: number; echec: number }; doublons_7j: number; followers: number
  }[]
  proxys: ProxyGeelark[]
  telephones: TelephoneGeelark[]
  sessions: SessionGeelark[]
  taches: TacheGeelark[]
  echecs: { code: number | null; texte: string | null; explication: string | null; n: number; dernier: string; telephones: string[] }[]
  warmup_jours: { jour: string; ok: number; echec: number; telephones: number }[]
}

export interface TelephoneRegie {
  id: string; nom: string; marche: string; modele: string; flotte: string | null; numero: string | null; actif: boolean
  comptes: { plateforme: string; pseudo: string | null; handle?: string | null; statut: string; phase: string | null; pret_le: string | null; raison: string | null }[]
  echecs_7j: number
  derniere_tache: { rpa: string; statut: string; erreur: string | null; debut: string | null; fin: string | null } | null
  verrou: { raison: string; expire_le: string } | null
  bilan_30j?: { reussies: number; echouees: number; secondes: number }
  /** Lu dans le journal de la tache : le statut GeeLark seul ne prouve rien. */
  derniere_ia?: { action: string; cible: string; raison: string; le: string } | null
  historique?: { rpa: string; statut: string; fin: string | null; erreur: string | null }[]
  derniere_chauffe?: { statut: string; fin: string | null; recherche: boolean; likes: number; commentaires: number; account_status?: boolean } | null
}
export interface LigneStat { marche: string; modele: string; reussies: number; echouees: number; secondes: number }
export interface StatsRegie {
  jours: (LigneStat & { jour: string })[]
  automatisations: (LigneStat & { rpa: string })[]
  erreurs: { erreur: string; marche: string; modele: string; n: number }[]
}
export interface ActionPlanifiee {
  telephone_id: string; rpa: string; prevue_le: string; statut: 'prevue' | 'prise' | 'faite' | 'echouee' | 'sautee' | 'annulee'
  tache: string | null; fin: string | null; note: string | null; plateforme: string; pseudo: string | null
}
export interface RegieEtat { etat: { genere_le: string; telephones: TelephoneRegie[]; stats?: StatsRegie; planning?: ActionPlanifiee[]; geelark?: { minutes: number; solde: number | null; releve_le: string; seuil: number } | null }; genere_le: string; recu_le: string }
export interface FarmReleve { jour: string; plateforme: string; pseudo: string; telephone_id: string; followers: number | null; posts_total: number | null; likes_total: number | null; erreur: string | null }
export interface FarmPost { plateforme: string; post_id: string; pseudo: string; publie_le: string; vues: number | null; likes: number | null }
export interface FarmGeelark { plan: Geelark['plan']; plan_le: string | null; minutes_7j: number; minutes_7j_prec: number }
export async function farmAnalytics(): Promise<{ releves: FarmReleve[]; posts: FarmPost[]; geelark?: FarmGeelark }> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('farm_analytics')
  if (error) throw error
  return data
}

/** Photo publiee par la regie (Mac) ; null tant qu elle n a rien publie. */
export async function regieLire(): Promise<RegieEtat | null> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('regie_lire')
  if (error) throw error
  return data as RegieEtat | null
}

export interface EcransFarm { allumes: string[]; demarrages: string[]; images: Record<string, string>; extinctions: Record<string, string>; le: string }
async function phoneFarm<T>(corps: Record<string, unknown>): Promise<T> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.functions.invoke('phone-farm', { body: corps })
  if (error) {
    const ctx = (error as { context?: Response }).context
    throw new Error((ctx ? await ctx.json().then((j: { erreur?: string }) => j.erreur).catch(() => null) : null) ?? error.message)
  }
  return data as T
}
export const farmEcrans = () => phoneFarm<EcransFarm>({ action: 'ecrans' })
export type GesteFarm =
  | { type: 'tap'; x: number; y: number }
  | { type: 'swipe'; x: number; y: number; x2: number; y2: number; ms: number }
  | { type: 'touche'; touche: 'retour' | 'accueil' | 'apps' }
  | { type: 'texte'; texte: string }
export const farmEcran = (telephone: string) => phoneFarm<{ image: string | null; le: string }>({ action: 'ecran', telephone })
export const farmGeste = (telephone: string, geste: GesteFarm) => phoneFarm<{ ok: true }>({ action: 'geste', telephone, geste })
export const farmCommander = (telephone: string, action: 'allumer' | 'eteindre') => phoneFarm<{ ok: true }>({ action, telephone })

export interface NoteCoffre { modele: string; chemin: string; type?: string; statut: string; date?: string; valide_par?: string; detail?: string }
export async function coffreLire(): Promise<{ commit: string; notes: NoteCoffre[]; recu_le: string } | null> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('coffre_lire')
  if (error) throw error
  return data
}

export type ActionRegie = 'chauffe' | 'nouvelle_identite' | 'creer_compte'
export interface CommandeRegie { id: string; action: ActionRegie; telephone_id: string; params: Record<string, unknown>; statut: 'en_attente' | 'en_cours' | 'faite' | 'echouee'; sortie: string | null; cree_le: string; debut: string | null; fin: string | null }
export async function regieCommander(action: ActionRegie, telephone: string, params: Record<string, unknown>): Promise<void> {
  if (!supabase) throw new Error('base non configuree')
  const { error } = await supabase.schema('traffic').rpc('regie_commander', { p_action: action, p_telephone: telephone, p_params: params })
  if (error) throw error
}
export async function regieCommandes(): Promise<CommandeRegie[]> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('regie_commandes_lire')
  if (error) throw error
  return data as CommandeRegie[]
}

export async function geelark(): Promise<Geelark> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('geelark')
  if (error) throw error
  // Pas de nombres() ici : les identifiants GeeLark (18 chiffres) perdraient
  // leur precision en devenant des nombres. La base rend deja des nombres JSON.
  return data as Geelark
}

export async function geelarkMajCompte(
  telephone: string, plateforme: string, handle: string, etat: EtatCompteGeelark, note: string,
  login?: { email?: string; mdp?: string; deuxfa?: string } | null,
) {
  if (!supabase) throw new Error('base non configuree')
  const { error } = await supabase.schema('traffic').rpc('geelark_maj_compte', {
    p_telephone: telephone, p_plateforme: plateforme, p_handle: handle, p_etat: etat, p_note: note,
    ...(login
      ? { p_login_email: login.email ?? '', p_login_mdp: login.mdp ?? '', p_login_2fa: login.deuxfa ?? '' }
      : {}),
  })
  if (error) throw error
}

/** Adresse publique du profil d un compte (Tinah 2026-10-19 : cliquer sur un compte ouvre son profil). */
export function urlProfil(plateforme: string, handle: string | null | undefined): string | null {
  if (!handle) return null
  const h = encodeURIComponent(handle.replace(/^@/, ''))
  return ({ instagram: `https://www.instagram.com/${h}/`, tiktok: `https://www.tiktok.com/@${h}`, threads: `https://www.threads.net/@${h}`,
    x: `https://x.com/${h}`, youtube: `https://www.youtube.com/@${h}`, reddit: `https://www.reddit.com/user/${h}` } as Record<string, string>)[plateforme] ?? null
}

// Chaine de la manageuse (2026-10-15) : VA -> telephones -> comptes -> liens -> stats. Jamais un montant.
export interface CompteEquipe {
  plateforme: string; handle: string | null; etat: EtatCompteGeelark; erreur: string | null
  followers: number | null; followers_7j: number | null; releve: string | null
  vues_jour: number; vues_7j: number; vues_7j_prec: number
}
export interface TelephoneEquipe {
  id: string; nom: string; marche: string | null; statut: number | null; allume_depuis: string | null
  va_id: string | null; lien_id: string | null; depuis: string | null; comptes: CompteEquipe[]
}
export interface LienEquipe {
  id: string; nom: string; code: string | null; url: string | null; actif: boolean; telephones: number
  page: { shortcode: string; url: string; visites_7j: number; clics_of_7j: number } | null
  clics_24h: number | null; clics_7j: number | null; subs_24h: number; subs_7j: number; subs_7j_prec: number
}
export interface VaEquipe {
  id: string; nom: string; marche: string | null; actif: boolean
  telephones: TelephoneEquipe[]; liens: LienEquipe[]; en_attente: { code: string; modele: string; depuis: string }[]
  totaux: {
    telephones: number; sans_lien: number; comptes: number; followers: number; followers_7j: number
    vues_jour: number; vues_7j: number; vues_7j_prec: number
    clics_24h: number; clics_7j: number; subs_24h: number; subs_7j: number; subs_7j_prec: number
  }
}
export interface TopContenu {
  plateforme: string; handle: string; url: string | null; publie_le: string | null; legende: string | null
  vues_jour: number; vues: number; telephone: string; va: string | null
}
export interface GeelarkEquipe {
  fraicheur: { releve: string | null; jour_releve: string | null; clics: string | null; subs: string | null }
  vas: VaEquipe[]; sans_va: TelephoneEquipe[]; top: TopContenu[]
}

export async function geelarkEquipe(): Promise<GeelarkEquipe> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('geelark_equipe')
  if (error) throw error
  return data as GeelarkEquipe
}

/** Donne des telephones a une VA (null : a personne), avec un lien que cette VA tient deja (null : sans lien). */
export async function affecterTelephones(telephones: string[], va: string | null, lien: string | null) {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('affecter_telephones', { p_telephones: telephones, p_va: va, p_lien: lien })
  if (error) throw error
  return data as number
}

export interface SerieFlotte { jour: string; followers: number | null; vues: number; posts: number; warmups: number }
export interface AnalytiqueFlotte {
  flotte: string | null; comptes: number; followers: number; followers_7j: number; vues_7j: number; vues_7j_prec: number
  posts_7j: number; posts_7j_prec: number; posts_rates_7j: number; warmups_7j: number; warmups_echec_7j: number
  vues_par_post_7j: number | null; serie: SerieFlotte[]
}
export interface PostAnalytique {
  plateforme: string; handle: string; url: string; publie_le: string; flotte: string | null; telephone: string | null
  legende: string | null; vues: number | null; likes: number | null; commentaires: number | null; partages: number | null
  engagement: number | null; gain_dernier_releve: number | null; courbe: (number | null)[] | null
}
export interface GeelarkAnalytics {
  fraicheur: string | null
  depuis: string | null
  jours: string[]
  flottes: AnalytiqueFlotte[]
  posts: PostAnalytique[]
  credits: { jour: number; plafond: number | null; solde: number | null }
  totaux: { comptes: number; followers: number; followers_7j: number; posts: number; posts_7j: number; vues_7j: number; vues_7j_prec: number }
  serie: { jour: string; vues: number; abonnes: number; posts: number; comptes_releves: number }[]
  comptes: {
    plateforme: 'instagram' | 'tiktok'; handle: string; telephone: string | null; nom: string | null; flotte: string | null
    followers: number | null; followers_7j: number | null; posts_total: number | null; likes_total: number | null
    prive: boolean | null; releve: string; vues_7j: number; erreur: string | null
    serie_followers: (number | null)[] | null; serie_vues: number[] | null
  }[]
  top_posts: { plateforme: string; handle: string; url: string; publie_le: string; legende: string | null; vues: number | null; likes: number | null; commentaires: number | null; gain_dernier_releve: number | null }[]
  erreurs: { plateforme: string; handle: string; erreur: string; le: string }[]
}

export async function geelarkAnalytics(): Promise<GeelarkAnalytics> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('geelark_analytics')
  if (error) throw error
  return data as GeelarkAnalytics
}

export type TypeAction = 'lancer_comptes' | 'reconnecter_instagram' | 'creer_tiktok' | 'connecter_tiktok' | 'threads_reprendre'
  | 'brander_instagram' | 'brander_tiktok' | 'passer_popups' | 'maj_apps'
export type TypeCommande = 'warmup' | 'posting_armer' | 'posting_desarmer' | 'posting_pause' | 'posting_reprendre' | TypeAction
export interface CommandeGeelark {
  id: string; type: TypeCommande; params: Record<string, any>; statut: 'en_attente' | 'en_cours' | 'fait' | 'refuse' | 'echec' | 'expiree'
  demande_le: string; pris_le: string | null; fini_le: string | null; resultat: string | null; par: string | null
  telephone: string | null; telephone_nom: string | null; marche: 'fr' | 'us' | null
  journal: string | null; etape: string | null; maj_le: string | null
}
/** Identifiants chiffres dans le navigateur (RSA-OAEP + AES-GCM) : seul le serveur les ouvre. */
export interface Chiffre { v: 1; cle: string; iv: string; donnees: string }
export interface CompteAPoster {
  compte: string; modele: string; telephone: string; formats: string[]; stock: number | null; reste: number | null
  postes: number | null; erreur: string | null; prochains?: { id: string; nom: string }[]
}
export interface EtatServeurGeelark {
  genere_le: string
  posting: {
    arme: boolean; pause_jusqua: string | null; stories_armees: boolean; journal: string[]
    creneaux: { marche: string; modeles: string[]; fuseau: string; heure: number; prochain: string; dernier_batch: string | null; fait_aujourdhui: boolean }[]
  } | null
  comptes_posting: CompteAPoster[] | null
  posts: { le: string; ok: boolean; compte: string | null; modele: string | null; telephone: string | null; format: string; media: string | null; legende: string | null; erreur: string | null }[] | null
  warmup: {
    en_cours: { marche: string; depuis: string; journal: string[]; action?: string | null }[]
    prevus: { marche: string; le: string; nouveaux?: boolean }[]
    derniers: { marche: string; fichier: string; le: string; reglages: string | null; bilan: string | null }[]
  } | null
  lancements?: {
    base_maj_le: string
    comptes: {
      id: number; platform: string; username: string; state: string; market: string | null; model: string | null; agency: string | null
      geelark_group: string | null; phone_id: string | null; attempts: number | null; warmup_day: number | null; last_error: string | null
      cree_le: string | null; maj_le: string | null; creations: number | null; sequenceur_depuis?: string | null
      dernier_evenement: { le: string; type: string; detail: string } | null
    }[]
  } | null
  // Par proxy (« serveur:port ») depuis le 15/10 ; ok null = test impossible (le service d IP ne repondait pas).
  proxys?: Record<string, { ok: boolean | null; serveur: string; ip: string | null; ms: number; erreur: string | null; le: string; essais?: number }>
  disque_libre_mo?: number
  erreurs: string[]
}
export interface GeelarkPilotage {
  serveur: { etat: EtatServeurGeelark; recu_le: string } | null
  commandes: CommandeGeelark[]
  /** Cle publique du serveur (SPKI en base64) : chiffre les identifiants avant envoi. */
  cle_publique: string | null
  /** Plan de branding Mia (branding_mia.py), par compte Instagram geefleet. */
  branding: Record<string, { handle: string; bio_ig: string; bio_tt: string; photo: number }>
}

export async function geelarkPilotage(): Promise<GeelarkPilotage> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('geelark_pilotage')
  if (error) throw error
  return data as GeelarkPilotage
}

export async function geelarkCommander(type: TypeCommande, params: Record<string, unknown> = {}, chiffre?: Chiffre): Promise<string> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('geelark_commander', { p_type: type, p_params: params, p_chiffre: chiffre ?? null })
  if (error) throw error
  return data as string
}

export async function geelarkAnnuler(id: string) {
  if (!supabase) throw new Error('base non configuree')
  const { error } = await supabase.schema('traffic').rpc('geelark_annuler', { p_id: id })
  if (error) throw error
}

export type TypeAFaire = 'doublon' | 'banni' | 'introuvable' | 'checkpoint' | 'reconnecter' | 'branding' | 'contenu' | 'creer_tiktok' | 'renseigner'
export interface PointAFaire {
  cle: string; type: TypeAFaire; rang: number; telephone: string | null; nom: string | null; flotte: string | null
  plateforme: string | null; handle: string | null; detail: string | null; depuis: string | null; etat: string | null
}
export interface GeelarkAFaire { points: PointAFaire[]; traites: { cle: string; statut: 'fait' | 'ignore'; note: string | null; le: string; par: string | null }[] }

export async function geelarkAFaire(): Promise<GeelarkAFaire> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('geelark_a_faire')
  if (error) throw error
  return data as GeelarkAFaire
}

export async function geelarkMarquer(cle: string, statut: 'fait' | 'ignore', note?: string) {
  if (!supabase) throw new Error('base non configuree')
  const { error } = await supabase.schema('traffic').rpc('geelark_marquer', { p_cle: cle, p_statut: statut, p_note: note ?? null })
  if (error) throw error
}

export interface PublicationGeelark {
  id: string; le: string; ok: boolean; compte: string | null; modele: string | null; flotte: string | null
  telephone: string | null; telephone_nom: string | null; handle: string | null; format: string | null
  media: string | null; media_id: string | null; legende: string | null; erreur: string | null
  stats: { url: string; vues: number | null; likes: number | null; commentaires: number | null; partages: number | null; releve: string; engagement: number | null } | null
}
export interface GeelarkPosts {
  serveur: { recu_le: string; posting: EtatServeurGeelark['posting']; comptes: CompteAPoster[] | null } | null
  passes: PublicationGeelark[]
  resume: { publies_7j: number; rates_7j: number; dernier: string | null }
}

export async function geelarkPosts(): Promise<GeelarkPosts> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('geelark_posts')
  if (error) throw error
  return data as GeelarkPosts
}

// --- Reddit ---------------------------------------------------------------------------

export type PhaseReddit = 'commenter' | 'poster' | 'promo' | 'pause' | 'banni' | 'suspendu'
export interface CompteReddit {
  id: string; pseudo: string; telephone: string | null; telephone_nom: string | null; modele: string | null
  lien: string | null; lien_nom: string | null; phase: PhaseReddit; karma_post: number | null; karma_commentaires: number | null
  karma_le: string | null; cree_le: string | null; note: string | null; maj_le: string
  posts_30j: number; ups_30j: number; retires_30j: number; dernier_post: string | null
}
export interface PostReddit {
  id: string; url: string; sub: string; titre: string | null; compte: string | null; pseudo: string | null; auteur: string | null
  publie_le: string | null; declare_le: string; ups: number | null; commentaires: number | null; ratio: number | null
  retire: string | null; releve_le: string | null; erreur: string | null; jouable: 'jouable' | 'a_juger' | 'ecarte' | null
  saturation: number | null; courbe: (number | null)[] | null
}
export interface TelephoneReddit {
  id: string; nom: string; remarque: string | null; statut: number | null; cree_le: string | null; proxy: string | null
  derniere_session: string | null; minutes_7j: number; sessions_7j: number; jours: { jour: string; minutes: number }[]
  taches_7j: { ok: number; echec: number; plans: string[] }; doublons_7j: number; comptes: string[]
}
export interface PointReddit {
  cle: string; type: 'inactif' | 'sans_compte' | 'lien_muet' | 'retire' | 'regle' | 'moteur' | 'tache'
  rang: number; telephone: string | null; nom: string; compte: string | null; detail: string | null; depuis: string | null
}
export interface Reddit {
  fraicheur: { liens: string | null; geelark: string | null; moteur: string | null; posts: string | null }
  svea: { id: string; nom: string; depuis: string | null; paie: { usd: number; par: string } | null } | null
  resume: {
    subs_7j: number; subs_7j_prec: number; subs_30j: number; revenu_30j: number; revenu_total: number; subs_total: number
    clics_7j: number; clics_depuis: string | null; telephones: number; telephones_actifs_7j: number; minutes_7j: number
    comptes: number; comptes_actifs: number; posts_7j: number; ups_7j: number; doublons_7j: number
  }
  serie: { jour: string; subs: number; revenu: number; clics: number | null; minutes: number; posts: number }[]
  liens: { id: string; nom: string; statut: string; cree_le: string | null; svea: boolean; subs_7j: number; subs_30j: number
    subs_total: number; revenu_total: number; clics_7j: number; dernier_sub: string | null; comptes: string[] }[]
  telephones: TelephoneReddit[]
  comptes: CompteReddit[]
  posts: PostReddit[]
  subs: { jouables: number; a_juger: number; ecartes: number; par_liste: Record<string, number> | null
    nos_subs: { sub: string; posts: number; ups_median: number | null; retires: number; verdict: string | null; saturation: number | null }[] }
  moteur: { cycle_le: string | null; recu_le: string; chiffres: Record<string, number> | null; urgences: { type: string; sub?: string; message?: string }[]
    backtest: { mesurable: boolean; posts_predits?: number; posts_temoins?: number; minimum?: number } | null
    rapports: { modele: string; date: string }[] } | null
  a_faire: PointReddit[]
  suivi: SuiviReddit
}
export type ADireReddit =
  | { type: 'telephones_eteints'; rang: number; telephones: { nom: string; depuis: string | null }[] }
  | { type: 'comptes_muets'; rang: number; jours: number; comptes: string[] }
  | { type: 'posts_retires'; rang: number; posts: { sub: string; pseudo: string | null; raison: string }[] }
  | { type: 'sub_trop_joue'; rang: number; max: number; subs: { sub: string; posts: number }[] }
  | { type: 'karma_a_jour'; rang: number; comptes: string[] }
  | { type: 'telephones_sans_compte'; rang: number; telephones: string[] }
  | { type: 'objectif'; rang: number; faits: number; objectif: number }
export interface SuiviReddit {
  activite: { jours: string[]; suivi_depuis: string | null; telephones: { id: string; nom: string; minutes: number[]; posts: number[]; comptes: string[] }[]; posts: number[]; subs: number[] }
  objectif: { posts_7j: number; objectif_7j: number; comptes_actifs: number }
  a_dire: ADireReddit[]
  acces: { person_id: string; email: string | null; lie: boolean; derniere_connexion: string | null } | null
}
export interface SubReddit {
  sub: string; marche: 'FR' | 'US'; nsfw: boolean; verdict: 'jouable' | 'a_juger' | 'ecarte'; niches: string[] | null
  actifs: number | null; saturation: number | null; ups: number | null; commentaires: number | null; photos: number | null
  autrices: number | null; posts: number | null; lien_dans_post: boolean | null; blocages: string | null
  exemples: { titre: string; ups: number | null; commentaires: number | null; type: string | null; lien: string | null }[] | null
  nos_posts: number
}
export interface SubsReddit { subs: SubReddit[]; rapports: { modele: string; date: string; contenu: string }[] | null }

export async function reddit(): Promise<Reddit> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('reddit')
  if (error) throw error
  return data as Reddit
}
export async function redditSubs(): Promise<SubsReddit> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('reddit_subs')
  if (error) throw error
  return data as SubsReddit
}
export async function redditMajCompte(c: { id: string | null; pseudo: string; telephone: string | null; modele: string | null; lien: string | null
  phase: PhaseReddit; karma_post: number | null; karma_commentaires: number | null; cree_le: string | null; note: string | null }): Promise<string> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('reddit_maj_compte', {
    p_id: c.id, p_pseudo: c.pseudo, p_telephone: c.telephone, p_modele: c.modele, p_lien: c.lien, p_phase: c.phase,
    p_karma_post: c.karma_post, p_karma_commentaires: c.karma_commentaires, p_cree_le: c.cree_le, p_note: c.note,
  })
  if (error) throw error
  return data as string
}
export async function redditAjouterPost(url: string, compte: string | null): Promise<string> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('reddit_ajouter_post', { p_url: url, p_compte: compte })
  if (error) throw error
  return data as string
}
export async function redditRetirerPost(id: string) {
  if (!supabase) throw new Error('base non configuree')
  const { error } = await supabase.schema('traffic').rpc('reddit_retirer_post', { p_id: id })
  if (error) throw error
}

// --- Analytics VA ---------------------------------------------------------------------

export interface DecisionVa { code: 'couper' | 'recadrer' | 'relancer' | 'qualite' | 'lien_mort' | 'pousser'; rang: number; texte: string }
export interface LigneVa {
  person_id: string; nom: string; manager: string | null; canal: string | null; actif: boolean; depuis: string | null
  statut: string | null; jours_sans_sub: number | null; dernier_sub: string | null; liens_actifs: number
  subs_24h: number; subs_24h_prec: number
  subs_7j: number; subs_7j_prec: number; subs_30j: number; subs_30j_prec: number; subs_total: number; subs_jour_7j: number
  part_30j: number | null; clics_7j: number; conversion_7j: number | null; revenu_30j: number; revenu_total: number
  depense_par_sub_30j: number | null; payants_pct_30j: number | null; cout_30j: number; cout_fixe?: boolean; rapporte_pour_1_usd: number | null
  subs_jour_30j: number; j7: number | null; j7_subs: number; marge_30j: number
  page: string | null; page_visites_7j: number | null; page_clics_7j: number | null; page_ctr_7j: number | null
  serie: number[]; semaines: number[]; decisions: DecisionVa[]
}
export interface ConstatVa { code: string; ton: 'ok' | 'attention' | 'alerte' | 'info'; titre: string; texte: string }
export interface AnalyticsVa {
  fraicheur: Record<string, string> | null
  reglages: { decisions: Record<string, number>; tarif: { usd_bas: number; usd_haut: number; seuil_ltv_par_sub_usd: number } | null; objectif_subs_jour: number }
  clics_depuis: string | null
  equipe: {
    subs_24h: number; subs_24h_prec: number
    subs_7j: number; subs_7j_prec: number; subs_30j: number; subs_30j_prec: number; revenu_30j: number; depense_par_sub_30j: number | null
    payants_pct_30j: number | null; cout_30j: number; cout_par_sub_30j: number | null; clics_7j: number; va: number; va_qui_ramenent_7j: number
    ltv_mediane: number | null; j7: number | null; j7_mediane: number | null; j7_payants_pct: number | null
    gms_visites_7j: number | null; gms_clics_of_7j: number | null; gms_visites_30j: number | null; gms_clics_of_30j: number | null; subs_7j_via_clics: number | null
  }
  projection: { mois: string; subs_mois: number; revenu_mois: number; jours_restants: number; rythme_7j: number; fin_de_mois: number; mois_precedent: number }
  unites: { va_mediane_subs_jour: number | null; cout_par_sub: number | null; revenu_par_sub_30j: number | null; j7_par_sub: number | null
    a_couper: number; a_couper_subs_jour: number }
  analyse: ConstatVa[]
  variation: { person_id: string; nom: string; delta: number; subs_7j: number; subs_7j_prec: number }[]
  rampe: { semaine: number; va: number; subs_jour: number; pct_actives: number }[]
  carte: { jour: number; heures: number[] }[]
  serie: { jour: string; subs: number; revenu: number; clics: number | null; visites: number | null; va: number }[]
  va: LigneVa[]
  semaines: string[]
  decisions: { cle: string; code: DecisionVa['code']; rang: number; person_id: string | null; nom: string; texte: string; lien: string | null }[]
  liens: { id: string; nom: string | null; canal: string; person_id: string | null; va: string | null; subs_7j: number; subs_30j: number
    clics_7j: number; conversion_7j: number | null; depense_par_sub: number | null; tarif: number | null; dernier_sub: string | null; cree_le: string | null }[]
  canaux: { canal: string; subs_30j: number; revenu_30j: number; depense_par_sub: number | null; va: number; j7: number | null }[]
  heures: number[]
  jours_semaine: number[]
}

export async function analyticsVa(): Promise<AnalyticsVa> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('analytics_va')
  if (error) throw error
  return data as AnalyticsVa
}

// --- Espace Reddit de la VA -----------------------------------------------------------

export interface SuggestionReddit {
  sub: string; heure: number | null; saturation: number | null; actifs: number | null; lien_dans_post: boolean | null
  photos: number | null; exemple: string | null; deja_semaine: number
}
export interface CompteEspace {
  id: string; pseudo: string; telephone: string | null; telephone_nom: string | null; phase: PhaseReddit
  karma_post: number | null; karma_commentaires: number | null; karma_le: string | null; cree_le: string | null; note: string | null
  lien: string | null; lien_nom: string | null; objectif_jour: number; posts_aujourdhui: number; posts_7j: number; ups_7j: number
  retires_30j: number; dernier_post: string | null; suggestions: SuggestionReddit[] | null
}
export interface EspaceReddit {
  personne: { id: string; nom: string }
  aujourdhui: string
  cadence: Record<string, number>
  regles: { fr: string; en: string }[]
  chiffres: { posts_7j: number; posts_7j_prec: number; objectif_7j: number; posts_aujourdhui: number; objectif_jour: number; ups_7j: number
    retires_30j: number; subs_7j: number; subs_7j_prec: number; clics_7j: number; minutes_7j: number }
  serie: { jour: string; posts: number; subs: number; minutes: number }[]
  telephones: { id: string; nom: string; remarque: string | null; statut: number | null; derniere_session: string | null; minutes_7j: number; jours: number[]; comptes: string[] }[]
  comptes: CompteEspace[]
  posts: { id: string; url: string; sub: string; titre: string | null; compte: string | null; pseudo: string | null; publie_le: string | null
    declare_le: string; ups: number | null; commentaires: number | null; retire: string | null; releve_le: string | null; erreur: string | null; courbe: number[] | null }[]
  liens: { id: string; nom: string }[]
  subs: { sub: string; nsfw: boolean; saturation: number | null; actifs: number | null; ups: number | null; photos: number | null; lien_dans_post: boolean | null
    niches: string[] | null; heure: number | null; heures: number[] | null; exemples: { titre: string; ups: number | null; lien: string | null }[]
    mes_posts_7j: number; mon_dernier_post: string | null }[]
}
export interface Espace { person_id: string; nom: string; role: string; espace: 'direction' | 'reddit' | 'manageuse' | null }

/** Qui est connecte, et quel ecran lui ouvrir (core.moi). */
export async function espace(): Promise<Espace | null> {
  if (!supabase) return null
  const { data, error } = await supabase.schema('core').rpc('moi')
  if (error) throw error
  return (data as Espace | null) ?? null
}
export async function redditEspace(personne?: string | null): Promise<EspaceReddit> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('reddit_espace', { p_personne: personne ?? null })
  if (error) throw error
  return data as EspaceReddit
}

/** Appel d une fonction serveur avec le jeton de la personne connectee ; l erreur de la fonction remonte telle quelle. */
async function fonction<T>(nom: string, corps: Record<string, unknown>): Promise<T> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.functions.invoke(nom, { body: corps })
  if (error) {
    const ctx = (error as { context?: Response }).context
    const detail = ctx ? await ctx.json().then((j: { erreur?: string }) => j.erreur).catch(() => null) : null
    throw new Error(detail ?? error.message)
  }
  return data as T
}

/** Donner a une VA l acces au dash (son espace) : l email se pose, le compte se cree, elle recoit un lien de connexion. */
export function donnerAcces(personne: string, email: string) {
  return fonction<{ email: string; lie: boolean; deja: boolean }>('acces-va', { person_id: personne, email })
}

// --- GetMySocial ----------------------------------------------------------------------

export type UsageGms = 'va' | 'reddit' | 'instagram' | 'tiktok' | 'youtube' | 'x' | 'autre'
export interface PageGms {
  id: string; shortcode: string; url: string; type: 'landing' | 'directlink'; nom: string; destination: string | null; plateforme: string | null
  groupe: string | null; statut: string; boutons: string[]; personne_id: string | null; personne: string | null; role: string | null
  telephone_id: string | null; telephone: string | null; compte: string | null; usage: UsageGms | null; rattache: 'auto' | 'main'
  parent_id: string | null; cree_par_dash: boolean; cree_le: string | null; visites_7j: number; visites_7j_prec: number; clics_7j: number
  clics_7j_prec: number; visites_30j: number; clics_30j: number; ctr_30j: number | null; derniere_visite: string | null; serie: number[]
}
/** Un lien de tracking Infloww, a choisir quand on cree une page GetMySocial. */
export interface LienOf {
  ref: string; lien_id: string; nom: string; canal: string; source: string | null; url: string
  /** La VA a qui le lien est affecte aujourd hui, s il l est. */
  va_id: string | null; va: string | null
  modele: string; marche: 'fr' | 'us' | null; subs: number; clics: number; cree_le: string | null; deja_page: boolean
}
export interface Gms {
  fraicheur: string | null
  fraicheur_liens: string | null
  liens_of: LienOf[]
  gabarits: Record<string, string>
  totaux: { visites_7j: number; visites_7j_prec: number; clics_7j: number; clics_7j_prec: number; visites_30j: number; clics_30j: number; pages: number; sans_proprietaire: number }
  serie: { jour: string; visites: number; clics: number }[]
  liens: PageGms[]
  personnes: { id: string; nom: string; role: string; actif: boolean }[]
  telephones: { id: string; nom: string; flotte: string | null }[]
}
export async function gms(): Promise<Gms> {
  if (!supabase) throw new Error('base non configuree')
  const { data, error } = await supabase.schema('traffic').rpc('gms')
  if (error) throw error
  return data as Gms
}
export interface SaisiePageGms {
  destination: string; destination_mym?: string | null; marche?: 'fr' | 'us' | null; nom: string; usage: UsageGms; gabarit?: string | null; shortcode?: string | null
  personne_id?: string | null; telephone_id?: string | null; compte?: string | null
}
export function gmsCreer(saisie: SaisiePageGms) {
  return fonction<{ url: string; shortcode: string; id: string; nom: string; gabarit: string; enfants: string[] }>('gms-lien', { ...saisie })
}
export function gmsRetirer(shortcode: string) {
  return fonction<{ retire: string; gms: boolean }>('gms-lien', { retirer: shortcode })
}
export async function gmsRattacher(lien: string, personne: string | null, telephone: string | null, compte: string | null, usage: UsageGms | null) {
  if (!supabase) throw new Error('base non configuree')
  const { error } = await supabase.schema('traffic').rpc('gms_rattacher', { p_lien: lien, p_personne: personne, p_telephone: telephone, p_compte: compte, p_usage: usage })
  if (error) throw error
}

// --- Fanvue (Isla, 2026-10-05) ------------------------------------------------------

export interface EtatFanvue { model_id: string; modele: string; connecte: boolean; handle: string | null; connecte_le: string | null; liens: number; subs: number; derniere_synchro: string | null }

/** Etat de la connexion Fanvue des modeles (direction). */
export async function fanvueEtat(): Promise<EtatFanvue[]> {
  if (!supabase) return []
  const { data, error } = await supabase.schema('traffic').rpc('fanvue_etat')
  if (error) throw error
  return (data ?? []) as EtatFanvue[]
}
/** Adresse d autorisation Fanvue a ouvrir avec le compte Fanvue de la modele (direction). */
export function fanvueConnecter(modelId?: string) {
  return fonction<{ url: string }>('fanvue-connect', modelId ? { model_id: modelId } : {})
}
/** Creer le lien Fanvue d une VA et le lui rattacher (direction ou sa manageuse). */
export function fanvueLien(vaId: string, plateforme: 'instagram' | 'tiktok' | 'reddit' | 'twitter', forcer = false) {
  return fonction<{ lien_id: string; url: string | null; nom: string; code: string; deja?: boolean }>('fanvue-lien', { va_id: vaId, plateforme, ...(forcer ? { forcer: true } : {}) })
}
